/* ==========================================================================
   Acadiana Made — global.js
   Native-first storefront runtime: drawers, mega menu, Ajax cart,
   predictive search, announcement rotation, free-shipping bar.
   ========================================================================== */
(function () {
  'use strict';

  var T = window.theme || {};
  var body = document.body;

  /* ------------------------------- helpers ------------------------------ */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  // The one money formatter. Handles all six Shopify format variants and adds
  // thousands separators, matching the Liquid `money` filter — so a client
  // rewrite of a price is byte-identical to the server render it replaces.
  // Promoted from quick-order-list.js (which had it right first); exposed as
  // window.AMFormatMoney so section inline scripts can defer to it at call
  // time instead of growing a fifth copy.
  function formatMoney(cents) {
    var fmt = T.moneyFormat || '${{amount}}';
    var m = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    var key = m ? m[1] : 'amount';
    var value = (parseInt(cents, 10) || 0) / 100;
    var thousands = ',', decimal = '.', precision = 2;
    switch (key) {
      case 'amount_no_decimals': precision = 0; break;
      case 'amount_with_comma_separator': thousands = '.'; decimal = ','; break;
      case 'amount_no_decimals_with_comma_separator': thousands = '.'; precision = 0; break;
      case 'amount_with_apostrophe_separator': thousands = "'"; break;
      case 'amount_no_decimals_with_space_separator': thousands = ' '; precision = 0; break;
      case 'amount_with_space_separator': thousands = ' '; decimal = ','; break;
    }
    var fixed = value.toFixed(precision).split('.');
    var whole = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    var out = precision ? whole + decimal + fixed[1] : whole;
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, out);
  }
  window.AMFormatMoney = formatMoney;

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  /* ------------------------------- Overlay ------------------------------ */
  var overlay = $('[data-overlay]');
  function showOverlay() { if (overlay) overlay.hidden = false; }
  function hideOverlay() { if (overlay) overlay.hidden = true; }

  /* ---------------------- Drawer / modal controller --------------------- */
  var openPanel = null;
  var lastTrigger = null;

  function trapFocus(e) {
    if (!openPanel || e.key !== 'Tab') return;
    var f = $all(FOCUSABLE, openPanel).filter(function (el) { return el.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function openDrawer(panel, trigger) {
    if (!panel) return;
    var returnTo = trigger || null;
    // A trigger inside the panel we're about to close (e.g. "Add to bag" inside
    // the compare or quick-view modal opening the cart drawer) goes inert with
    // it — return focus to that panel's own opener instead.
    if (trigger && openPanel && openPanel !== panel && openPanel.contains(trigger)) returnTo = lastTrigger;
    closeDrawer();
    openPanel = panel;
    lastTrigger = returnTo;
    panel.classList.add('is-open');
    panel.removeAttribute('inert');
    panel.setAttribute('aria-hidden', 'false');
    // Only elements that declare themselves as disclosure controls get the
    // state — an "Add to bag" button that happens to open the drawer isn't one.
    if (trigger && trigger.hasAttribute('aria-expanded')) trigger.setAttribute('aria-expanded', 'true');
    body.classList.add('locked');
    showOverlay();
    var focusEl = panel.querySelector('[data-drawer-initial-focus]') || panel.querySelector(FOCUSABLE);
    if (focusEl) window.requestAnimationFrame(function () { focusEl.focus(); });
    document.addEventListener('keydown', trapFocus);
  }

  // Responsive drawers (collection filters) are an off-canvas drawer on
  // mobile but an always-visible sidebar on desktop. Only make them inert /
  // aria-hidden while actually off-canvas.
  var mobileMq = window.matchMedia('(max-width: 47.9375rem)');
  function syncResponsiveDrawers() {
    $all('[data-responsive-drawer]').forEach(function (p) {
      if (mobileMq.matches && !p.classList.contains('is-open')) {
        p.setAttribute('inert', '');
        p.setAttribute('aria-hidden', 'true');
      } else {
        p.removeAttribute('inert');
        p.removeAttribute('aria-hidden');
      }
    });
  }
  syncResponsiveDrawers();
  if (mobileMq.addEventListener) mobileMq.addEventListener('change', syncResponsiveDrawers);
  else if (mobileMq.addListener) mobileMq.addListener(syncResponsiveDrawers);

  function closeDrawer() {
    if (!openPanel) return;
    openPanel.classList.remove('is-open');
    // Only true off-canvas drawers get inert; responsive ones are handled by
    // syncResponsiveDrawers().
    if (openPanel.classList.contains('drawer')) {
      openPanel.setAttribute('inert', '');
      openPanel.setAttribute('aria-hidden', 'true');
    }
    syncResponsiveDrawers();
    // Reset only the openers of the panel we're actually closing. A blanket
    // reset across every [aria-controls] on the page also flipped unrelated
    // disclosures (cart tools, lookbook hotspots) whose handlers read
    // aria-expanded as their state — their next press then did nothing.
    if (openPanel.id) {
      $all('[aria-controls="' + openPanel.id + '"][aria-expanded]').forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
    }
    if (lastTrigger && lastTrigger.hasAttribute('aria-expanded')) lastTrigger.setAttribute('aria-expanded', 'false');
    body.classList.remove('locked');
    hideOverlay();
    document.removeEventListener('keydown', trapFocus);
    if (lastTrigger) lastTrigger.focus();
    openPanel = null;
    lastTrigger = null;
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      var panel = $('#' + opener.getAttribute('data-drawer-open'));
      openDrawer(panel, opener);
      if (panel && panel.hasAttribute('data-cart-drawer')) refreshCart();
      return;
    }
    if (e.target.closest('[data-drawer-close]')) { e.preventDefault(); closeDrawer(); return; }
  });
  if (overlay) overlay.addEventListener('click', closeDrawer);
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeDrawer(); });

  /* ------------------------------ Mega menu ----------------------------- */
  (function megaMenu() {
    var triggers = $all('[data-mega-trigger]');
    if (!triggers.length) return;
    var header = $('[data-header]');
    function closeAll() {
      $all('[data-mega-panel]').forEach(function (p) { p.setAttribute('data-open', 'false'); });
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
    }
    function open(key) {
      closeAll();
      var panel = $('[data-mega-panel="' + key + '"]');
      var trigger = $('[data-mega-trigger="' + key + '"]');
      if (panel) panel.setAttribute('data-open', 'true');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }
    triggers.forEach(function (t) {
      var key = t.getAttribute('data-mega-trigger');
      // Touch taps synthesize mouseenter + focus before click, which would mark
      // the panel open and make the first tap navigate. Pointer events reveal
      // the real modality, so only mouse/pen hover and keyboard focus pre-open.
      var touching = false;
      t.addEventListener('pointerdown', function (e) { touching = e.pointerType === 'touch'; });
      t.addEventListener('pointerenter', function (e) { if (e.pointerType !== 'touch') open(key); });
      t.addEventListener('focus', function () { if (!touching) open(key); });
      t.addEventListener('click', function (e) {
        var href = t.getAttribute('href');
        if (t.tagName === 'A' && href && href !== '#') {
          // Link triggers navigate on click; a touch tap opens the panel first
          // and a second tap follows the link.
          if (touching && t.getAttribute('aria-expanded') !== 'true') {
            e.preventDefault();
            open(key);
          }
          touching = false;
          return;
        }
        touching = false;
        e.preventDefault();
        if (t.getAttribute('aria-expanded') === 'true') closeAll(); else open(key);
      });
    });
    if (header) {
      header.addEventListener('mouseleave', closeAll);
      header.addEventListener('keydown', function (e) { if (e.key === 'Escape') closeAll(); });
      // Keyboard users: close when focus leaves the header entirely.
      header.addEventListener('focusout', function (e) {
        if (!e.relatedTarget || !header.contains(e.relatedTarget)) closeAll();
      });
    }
    // Click/tap outside the header closes any open panel.
    document.addEventListener('click', function (e) {
      if (header && !header.contains(e.target)) closeAll();
    });
  })();

  /* ------------------------- Announcement rotation ---------------------- */
  // The swap hides the outgoing slide with [hidden]. If the shopper is
  // standing on the link inside it, hiding it under them drops focus to
  // <body> — the next Tab restarts from the skip link — or slides the link
  // out from under the cursor mid-click. So the rotation holds while focus or
  // the pointer is inside the track, and starts stopped for a shopper who
  // asked for reduced motion (the hero's video toggle does the same).
  (function announcement() {
    var track = $('[data-announcement-track]');
    if (!track) return;
    var slides = $all('[data-announcement-slide]', track);
    if (slides.length < 2) return;
    var pauseBtn = $('[data-announcement-pause]');
    var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var i = 0, timer = null, held = false, userSet = false;
    var paused = !!(reduce && reduce.matches);

    function show(n) { slides.forEach(function (s, k) { s.hidden = k !== n; }); }
    function tick() {
      if (paused || held) return;
      i = (i + 1) % slides.length;
      show(i);
    }
    function sync() {
      if (pauseBtn) {
        pauseBtn.setAttribute('aria-pressed', String(paused));
        var label = pauseBtn.getAttribute(paused ? 'data-label-play' : 'data-label-pause');
        if (label) pauseBtn.setAttribute('aria-label', label);
      }
      if (paused) { if (timer) { clearInterval(timer); timer = null; } }
      else if (!timer) timer = setInterval(tick, 5000);
    }

    show(0);
    track.addEventListener('mouseenter', function () { held = true; });
    track.addEventListener('mouseleave', function () { held = false; });
    track.addEventListener('focusin', function () { held = true; });
    track.addEventListener('focusout', function (e) {
      if (!track.contains(e.relatedTarget)) held = false;
    });
    if (pauseBtn) pauseBtn.addEventListener('click', function () {
      userSet = true;
      paused = !paused;
      sync();
    });
    // Reduced motion switched on mid-visit stops it, unless the shopper has
    // already made the call with the button.
    if (reduce && reduce.addEventListener) reduce.addEventListener('change', function () {
      if (userSet) return;
      paused = reduce.matches;
      sync();
    });
    sync();
  })();

  /* ------------------------------- Ajax cart ---------------------------- */
  function updateCartCount(count) {
    $all('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  // Two bars can be on the page at once: the cart drawer's and the cart page's
  // (server-rendered inside the totals part, so it is right with JS off). Both
  // describe the same bag, so both move together.
  function renderFreeShipping(total) {
    var bars = $all('[data-free-shipping]');
    if (!bars.length) return;
    var threshold = parseFloat(T.freeShippingThreshold) * 100;
    var valid = !!threshold && !isNaN(threshold);
    var remaining = valid ? threshold - total : 0;
    bars.forEach(function (bar) {
      if (!valid) { bar.hidden = true; return; }
      bar.hidden = false;
      var fill = bar.querySelector('[data-free-shipping-fill]');
      var msg = bar.querySelector('[data-free-shipping-msg]');
      if (remaining <= 0) {
        if (msg) msg.textContent = bar.getAttribute('data-msg-reached') || 'Free shipping unlocked';
        if (fill) fill.style.width = '100%';
      } else {
        if (msg) msg.textContent = (bar.getAttribute('data-msg-progress') || '[[amount]] away from free shipping').replace('[[amount]]', formatMoney(remaining));
        if (fill) fill.style.width = Math.min(100, (total / threshold) * 100) + '%';
      }
    });
  }

  function esc(str) { return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }

  /* ---------------------------- Cart upsell ----------------------------- */
  // "You might like" inside the drawer: recommendations for the first line
  // item (Section Rendering API on /recommendations/products), falling back to
  // a merchant-chosen collection. Cached per key so cart edits don't refetch.
  //
  // The fetched markup is kept in upsellHtml and the rail is *repainted* from
  // it on every cart change, rather than having rows deleted out of the live
  // DOM. Deleting was one-way: a product added from the rail was pruned
  // correctly, but removing it from the bag again never brought it back — the
  // row only returned when the first line item changed, or on a page reload.
  // Do that two or three times and the rail emptied itself and the whole
  // "You might like" block disappeared from a drawer that still had a bag in it.
  var upsellKey = null, upsellHtml = '', upsellMark = null, upsellInCart = [];
  // Repaint from the fetched copy, then drop whatever is already in the bag.
  // Reads upsellInCart rather than closing over one call's list, so a response
  // that lands after a later cart change still prunes against the current bag.
  // Skipped when the bag holds the same products as last time, so an unrelated
  // cart change (quantity, note, discount) doesn't churn the DOM.
  function paintCartUpsell() {
    var box = $('[data-cart-upsell]');
    if (!box) return;
    var mark = upsellInCart.slice().sort().join(',');
    if (mark === upsellMark) return;
    upsellMark = mark;
    box.innerHTML = upsellHtml;
    $all('[data-cart-upsell-item]', box).forEach(function (li) {
      if (upsellInCart.indexOf(li.getAttribute('data-cart-upsell-item')) !== -1) li.remove();
    });
    box.hidden = !box.querySelector('[data-cart-upsell-item]');
    if (window.AMWishlist) window.AMWishlist.sync(box);
  }
  function loadCartUpsell(cart) {
    var box = $('[data-cart-upsell]');
    if (!box) return;
    var limit = box.getAttribute('data-limit') || 4;
    var fallback = box.getAttribute('data-fallback-url');
    var first = cart.items && cart.items.length ? cart.items[0] : null;
    var key = first ? 'p' + first.product_id : (fallback ? 'c' : '');
    upsellInCart = (cart.items || []).map(function (i) { return i.handle; });
    if (!key) { box.hidden = true; upsellMark = null; return; }
    if (key === upsellKey) { paintCartUpsell(); return; }
    upsellKey = key;
    function fetchSection(url) {
      return fetch(url, { headers: { 'Accept': 'text/html' } }).then(function (r) { return r.ok ? r.text() : ''; }).then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var inner = doc.querySelector('[data-cart-upsell-inner]');
        return inner && inner.querySelector('[data-cart-upsell-item]') ? inner.innerHTML : '';
      }).catch(function () { return ''; });
    }
    var recsUrl = first ? box.getAttribute('data-recs-url') + '?section_id=cart-upsell&product_id=' + first.product_id + '&limit=' + limit + '&intent=related' : null;
    var p = recsUrl ? fetchSection(recsUrl) : Promise.resolve('');
    p.then(function (html) {
      if (!html && fallback) return fetchSection(fallback + '?section_id=cart-upsell');
      return html;
    }).then(function (html) {
      if (upsellKey !== key) return; // superseded
      upsellHtml = html;
      upsellMark = null; // a fresh rail always paints
      paintCartUpsell();
    });
  }

  function renderCart(cart) {
    updateCartCount(cart.item_count);
    loadCartUpsell(cart);
    renderFreeShipping(cart.total_price);
    var container = $('[data-cart-lines]');
    var subtotalEls = $all('[data-cart-subtotal]');
    subtotalEls.forEach(function (el) { el.textContent = formatMoney(cart.total_price); });
    $all('[data-cart-checkout]').forEach(function (btn) { btn.toggleAttribute('disabled', cart.item_count === 0); });

    var emptyFeatured = $('[data-cart-empty-featured]');
    if (emptyFeatured) emptyFeatured.hidden = cart.item_count !== 0;
    if (!container) return;
    if (cart.item_count === 0) {
      container.innerHTML = '<p class="cart-drawer__empty">' + (T.strings.cartEmpty || 'Your bag is empty.') + '</p>';
      return;
    }
    container.innerHTML = cart.items.map(function (item) {
      var img = item.image ? item.image.replace(/(\.[a-z]+)(\?.*)?$/i, '_160x$1$2') : '';
      var opts = (item.options_with_values || []).map(function (o) { return esc(o.value); }).join(' · ');
      var discounts = (item.line_level_discount_allocations || []).filter(function (d) { return d.amount > 0; }).map(function (d) {
        return '<p class="cart-line__discount">' + esc(d.discount_application && d.discount_application.title) + ' &minus;' + formatMoney(d.amount) + '</p>';
      }).join('');
      var props = Object.keys(item.properties || {}).filter(function (k) { return k.charAt(0) !== '_' && item.properties[k]; }).map(function (k) {
        return '<p class="cart-line__variant">' + esc(k) + ': ' + esc(item.properties[k]) + '</p>';
      }).join('');
      var lineTotal = item.original_line_price > item.final_line_price
        ? '<s class="cart-line__compare">' + formatMoney(item.original_line_price) + '</s> ' + formatMoney(item.final_line_price)
        : formatMoney(item.final_line_price);
      return '' +
        '<article class="cart-line" data-line-item>' +
          '<a class="cart-line__media" href="' + esc(item.url) + '">' +
            (img ? '<img src="' + esc(img) + '" alt="' + esc(item.product_title) + '" width="80" height="100" loading="lazy">' : '') +
          '</a>' +
          '<div class="cart-line__info">' +
            '<a class="cart-line__title" href="' + esc(item.url) + '">' + esc(item.product_title) + '</a>' +
            (opts ? '<p class="cart-line__variant">' + opts + '</p>' : '') + props + discounts +
            '<div class="cart-line__controls">' +
              '<div class="qty" data-qty>' +
                '<button type="button" class="qty__btn" data-qty-down aria-label="' + esc(T.strings.decreaseQty || 'Decrease quantity') + '">&minus;</button>' +
                '<input class="qty__input" type="number" min="0" value="' + item.quantity + '" data-line-key="' + item.key + '" aria-label="' + esc(T.strings.quantity || 'Quantity') + '">' +
                '<button type="button" class="qty__btn" data-qty-up aria-label="' + esc(T.strings.increaseQty || 'Increase quantity') + '">+</button>' +
              '</div>' +
              (T.quickEditCart && item.options_with_values && item.options_with_values.length && item.variant_title !== 'Default Title'
                ? '<button type="button" class="cart-line__edit" data-line-edit data-line-key="' + esc(item.key) + '" data-line-variant="' + item.variant_id + '" data-line-qty="' + item.quantity + '" data-line-url="' + esc(item.url.split('?')[0]) + '" data-line-props="' + esc(JSON.stringify(item.properties || {})) + '">' + esc(T.strings.edit || 'Edit') + '</button>'
                : '') +
              '<button type="button" class="cart-line__remove" data-line-remove="' + item.key + '">' + esc(T.strings.remove || 'Remove') + '</button>' +
            '</div>' +
          '</div>' +
          '<div class="cart-line__price">' + lineTotal + '</div>' +
        '</article>';
    }).join('');
    var discountTotal = $('[data-cart-discount-total]');
    if (discountTotal) {
      var saved = cart.total_discount || 0;
      discountTotal.hidden = !saved;
      var amt = discountTotal.querySelector('[data-cart-discount-amount]');
      if (amt) amt.textContent = '\u2212' + formatMoney(saved);
    }
    syncGiftButtons(cart);
    renderDiscountCodes(cart);
  }

  function refreshCart() {
    return fetch(T.routes.cart + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(renderCart)
      .catch(function () {});
  }

  var isCartPage = !!document.querySelector('.cart-page');

  // The cart page is server-rendered with no per-line data hooks, so cart
  // mutations re-render it through the Section Rendering API instead of a
  // full reload: changeLine() bundles the section id into its POST, other
  // paths refetch it (updateCartPage). The swap replaces only the "lines"
  // and "totals" parts when both sides have them — open tool panels, a
  // half-typed note, and the terms tick survive — and falls back to the
  // whole section on the filled/empty transition. All cart handlers are
  // delegated, so swapped markup needs no rebinding.
  function cartPageSectionEl() { return $('[data-cart-page-section]'); }
  var cartPageReq = 0;
  function swapCartPage(html) {
    var el = cartPageSectionEl();
    if (!el || !html) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var next = doc.querySelector('[data-cart-page-section]');
    if (!next) return;
    var parts = ['lines', 'totals'];
    var have = parts.map(function (p) {
      return { cur: el.querySelector('[data-cart-page-part="' + p + '"]'), next: next.querySelector('[data-cart-page-part="' + p + '"]') };
    });
    if (have.every(function (h) { return h.cur && h.next; })) {
      have.forEach(function (h) { h.cur.innerHTML = h.next.innerHTML; });
    } else {
      el.innerHTML = next.innerHTML;
      syncTerms(); // the server renders the terms box unchecked; restore this session's tick
    }
  }
  function updateCartPage() {
    var el = cartPageSectionEl();
    if (!el) return Promise.resolve();
    var req = ++cartPageReq;
    var url = window.location.pathname + '?section_id=' + encodeURIComponent(el.getAttribute('data-cart-page-section'));
    return fetch(url, { headers: { 'Accept': 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) { if (req === cartPageReq) swapCartPage(html); })
      .catch(function () {});
  }
  // Busy feedback while a cart mutation is in flight: dim the drawer lines and
  // the cart page, and freeze the specific line being changed (both copies of
  // it — drawer and page — carry the same data-line-key).
  function setCartBusy(on, key) {
    $all('[data-cart-lines], [data-cart-page-section]').forEach(function (el) {
      if (on) el.setAttribute('aria-busy', 'true'); else el.removeAttribute('aria-busy');
    });
    if (key == null) return;
    $all('input[data-line-key]').forEach(function (input) {
      if (input.getAttribute('data-line-key') !== key) return;
      var line = input.closest('[data-line-item]');
      if (line) line.classList.toggle('is-busy', !!on);
    });
  }

  var liveRegion = document.getElementById('a11y-status') || (function () {
    var el = document.createElement('div');
    el.id = 'a11y-status'; el.className = 'visually-hidden'; el.setAttribute('role', 'status'); el.setAttribute('aria-live', 'polite');
    document.body.appendChild(el); return el;
  })();
  function announce(msg) { liveRegion.textContent = ''; window.setTimeout(function () { liveRegion.textContent = msg; }, 50); }
  var lineReq = 0;
  function changeLine(key, quantity) {
    if (isNaN(quantity) || quantity < 0) quantity = 0;
    var req = ++lineReq;
    setCartBusy(true, key);
    var body = { id: key, quantity: quantity };
    var pageEl = cartPageSectionEl();
    if (pageEl) {
      body.sections = pageEl.getAttribute('data-cart-page-section');
      body.sections_url = window.location.pathname;
    }
    return fetch(T.routes.cartChange, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); }).then(function (data) {
      if (req !== lineReq) return; // superseded by a newer change; let that one render
      setCartBusy(false, key);
      if (data.status || !data.items) {
        announce(data.description || data.message || 'Could not update your bag.');
        return refreshCart().then(updateCartPage);
      }
      if (data.sections && pageEl) {
        cartPageReq++; // anything still in flight from updateCartPage is now stale
        swapCartPage(data.sections[pageEl.getAttribute('data-cart-page-section')]);
      }
      renderCart(data);
      announce(T.strings.bagUpdated || 'Bag updated');
    }).catch(function () {
      if (req !== lineReq) return;
      setCartBusy(false, key);
      refreshCart().then(updateCartPage);
    });
  }

  // Delegated cart-line controls
  document.addEventListener('click', function (e) {
    var remove = e.target.closest('[data-line-remove]');
    if (remove) { changeLine(remove.getAttribute('data-line-remove'), 0); return; }
    var up = e.target.closest('[data-qty-up]');
    var down = e.target.closest('[data-qty-down]');
    if (up || down) {
      var wrap = (up || down).closest('[data-qty]');
      var input = wrap && wrap.querySelector('input[data-line-key]');
      if (!input) return;
      var q = (parseInt(input.value, 10) || 0) + (up ? 1 : -1);
      if (q < 0) q = 0;
      changeLine(input.getAttribute('data-line-key'), q);
    }
  });
  document.addEventListener('change', function (e) {
    var input = e.target.closest('input[data-line-key]');
    if (input) changeLine(input.getAttribute('data-line-key'), parseInt(input.value, 10) || 0);
  });

  /* ------------------------------ Cart tools ---------------------------- */
  // Note · gift add-on · shipping estimate · discount code (snippets/cart-tools),
  // plus the "I agree to the terms" gate (snippets/cart-terms). Everything is
  // delegated so it works in the drawer and on the cart page.
  var CS = T.strings;
  function cartUpdate(payload) {
    // keepalive: these fire from a `change` that the shopper triggered by
    // clicking something that navigates (Checkout, "View bag"). A plain fetch
    // is cancelled the moment the browser leaves; keepalive lets it land.
    return fetch(T.routes.cartUpdate, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify(payload) })
      .then(function (r) { return r.json(); });
  }

  // The drawer's note and gift message post with checkout.
  // On the cart page the tools block is INSIDE the cart form, so the checkout
  // submit carries `note` and `attributes[…]` itself. In the drawer it is not:
  // .cart-drawer__checkout-form holds only the button, and the tools sit above
  // it. That left the async cartUpdate above as the only way those fields ever
  // reached the server — and it is started by the blur that fires as the
  // shopper clicks Checkout, so the navigation raced it and a note typed and
  // never blurred-early enough was simply dropped. Mirroring the fields into
  // the checkout form makes the checkout POST carry them regardless. An
  // untouched field mirrors the value the server already rendered, so posting
  // it back is a no-op.
  function mirrorDrawerCartFields() {
    var form = $('.cart-drawer__checkout-form');
    if (!form) return;
    var box = form.querySelector('[data-cart-mirror]');
    if (!box) {
      box = document.createElement('div');
      box.setAttribute('data-cart-mirror', '');
      form.insertBefore(box, form.firstChild);
    }
    var fields = [];
    var note = $('[data-cart-drawer] .cart-note__field');
    if (note) fields.push({ name: 'note', value: note.value });
    $all('[data-cart-drawer] [data-cart-attr]').forEach(function (f) {
      var key = f.getAttribute('data-cart-attr');
      if (key) fields.push({ name: 'attributes[' + key + ']', value: f.value });
    });
    box.innerHTML = '';
    fields.forEach(function (f) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = f.name;
      input.value = f.value;
      box.appendChild(input);
    });
  }
  // The note and each cart attribute have two fields on the cart page — the
  // drawer's copy and the page's — both server-rendered from the same value
  // and neither aware of the other. Editing one used to leave the other
  // showing the old text, and now that the drawer's copy posts with checkout,
  // a stale copy would post over a fresh one. Keep the twins equal.
  function syncCartFieldTwins(source) {
    // Matched by attribute VALUE rather than a built selector: the attribute
    // holds a translated label ("Gift message"), and a quote or bracket in a
    // translation would turn a selector string into a syntax error.
    var isNote = source.classList.contains('cart-note__field');
    var key = source.getAttribute('data-cart-attr');
    $all(isNote ? '.cart-note__field' : '[data-cart-attr]').forEach(function (f) {
      if (f === source) return;
      if (!isNote && f.getAttribute('data-cart-attr') !== key) return;
      f.value = source.value;
    });
  }
  document.addEventListener('input', function (e) {
    var field = e.target.closest('.cart-note__field, [data-cart-attr]');
    if (!field) return;
    syncCartFieldTwins(field);
    mirrorDrawerCartFields();
  });
  mirrorDrawerCartFields();
  // Toggle panels (one open at a time per tools block)
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-cart-tool]');
    if (!btn) return;
    var tools = btn.closest('[data-cart-tools]');
    var name = btn.getAttribute('data-cart-tool');
    var panel = tools.querySelector('[data-cart-tool-panel="' + name + '"]');
    var open = btn.getAttribute('aria-expanded') === 'true';
    $all('[data-cart-tool]', tools).forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    $all('[data-cart-tool-panel]', tools).forEach(function (p) { p.hidden = true; });
    if (!open && panel) {
      btn.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      var f = panel.querySelector('textarea, input:not([type="hidden"]), select, button');
      if (f) f.focus({ preventScroll: true });
      if (name === 'ship') initShipProvinces(panel);
    }
  });
  // Cart note — save on change
  document.addEventListener('change', function (e) {
    var note = e.target.closest('.cart-note__field');
    if (!note || !T.routes.cartUpdate) return;
    var status = note.parentNode.querySelector('[data-cart-note-status]');
    cartUpdate({ note: note.value }).then(function () {
      if (status) { status.textContent = CS.saved || 'Saved'; window.setTimeout(function () { status.textContent = ''; }, 2000); }
    }).catch(function () {});
  });
  // Cart attributes (gift message etc.) — save on change
  document.addEventListener('change', function (e) {
    var field = e.target.closest('[data-cart-attr]');
    if (!field || !T.routes.cartUpdate) return;
    var attrs = {}; attrs[field.getAttribute('data-cart-attr')] = field.value;
    cartUpdate({ attributes: attrs }).catch(function () {});
  });
  // Gift add-on: add the configured product once
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-cart-gift-add]');
    if (!btn) return;
    var panel = btn.closest('[data-cart-tool-panel]');
    var variant = panel && panel.getAttribute('data-gift-variant');
    if (!variant) return;
    btn.setAttribute('disabled', '');
    setCartBusy(true);
    fetch(T.routes.cartAdd, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: [{ id: parseInt(variant, 10), quantity: 1 }] }) })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status) throw new Error(data.description || 'Error');
        announce(CS.giftAdded || 'Gift wrap added');
        return refreshCart().then(updateCartPage).then(function () { setCartBusy(false); });
      })
      .catch(function (err) { setCartBusy(false); btn.removeAttribute('disabled'); announce((err && err.message) || 'Error'); });
  });
  function syncGiftButtons(cart) {
    $all('[data-cart-tool-panel="gift"]').forEach(function (panel) {
      var v = parseInt(panel.getAttribute('data-gift-variant'), 10);
      var inCart = (cart.items || []).some(function (i) { return i.variant_id === v; });
      var btn = panel.querySelector('[data-cart-gift-add]');
      if (!btn) return;
      btn.toggleAttribute('disabled', inCart);
      btn.textContent = inCart ? (btn.getAttribute('data-label-added') || 'Added') : (btn.getAttribute('data-label-add') || 'Add');
    });
  }
  // Shipping estimate: country -> provinces (from country_option_tags data), then /cart/shipping_rates.json
  function initShipProvinces(panel) {
    var country = panel.querySelector('[data-ship-country]');
    if (!country || country.hasAttribute('data-ready')) return;
    country.setAttribute('data-ready', '');
    var provWrap = panel.querySelector('[data-ship-province-wrap]'), prov = panel.querySelector('[data-ship-province]');
    function fill() {
      var opt = country.options[country.selectedIndex];
      var list = [];
      try { list = JSON.parse((opt && opt.getAttribute('data-provinces')) || '[]'); } catch (err) { list = []; }
      if (prov) prov.innerHTML = list.map(function (p) { return '<option value="' + esc(p[0]) + '">' + esc(p[1]) + '</option>'; }).join('');
      if (provWrap) provWrap.hidden = !list.length;
    }
    // Default to the shop's country if the select has no explicit selection
    if (T.shopCountry && !country.querySelector('option[selected]')) {
      var m = Array.prototype.find.call(country.options, function (o) { return o.value === T.shopCountry; });
      if (m) country.value = m.value;
    }
    country.addEventListener('change', fill);
    fill();
  }
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-cart-ship]');
    if (!form) return;
    e.preventDefault();
    var panel = form.closest('[data-cart-tool-panel]'), out = panel.querySelector('[data-ship-result]');
    var country = form.querySelector('[data-ship-country]'), prov = form.querySelector('[data-ship-province]'), zip = form.querySelector('[data-ship-zip]');
    var q = new URLSearchParams();
    q.set('shipping_address[country]', country ? country.value : '');
    if (prov && prov.value && !prov.closest('[hidden]')) q.set('shipping_address[province]', prov.value);
    if (zip && zip.value.trim()) q.set('shipping_address[zip]', zip.value.trim());
    out.innerHTML = '<p class="cart-tools__hint">' + esc(CS.calculating || 'Calculating…') + '</p>';
    var url = T.routes.cart + '/shipping_rates.json?' + q.toString();
    var attempts = 0;
    function poll() {
      return fetch(url, { headers: { 'Accept': 'application/json' } }).then(function (r) {
        if (r.status === 202 && attempts++ < 6) return new Promise(function (res) { window.setTimeout(res, 700); }).then(poll);
        return r.json().then(function (data) { return { ok: r.ok, data: data }; });
      });
    }
    poll().then(function (res) {
      var data = res.data || {};
      if (!res.ok || !data.shipping_rates) {
        var msg = Object.keys(data).map(function (k) { return k.replace(/_/g, ' ') + ' ' + [].concat(data[k]).join(', '); }).join('. ');
        out.innerHTML = '<p class="cart-tools__hint cart-tools__hint--error">' + esc(msg || CS.shipError || 'Could not estimate shipping.') + '</p>';
        return;
      }
      if (!data.shipping_rates.length) { out.innerHTML = '<p class="cart-tools__hint">' + esc(CS.noRates || 'No shipping rates for that address.') + '</p>'; return; }
      out.innerHTML = '<ul class="cart-ship__rates" role="list">' + data.shipping_rates.map(function (r) {
        var price = parseFloat(r.price) === 0 ? (CS.free || 'Free') : formatMoney(Math.round(parseFloat(r.price) * 100));
        return '<li class="cart-ship__rate"><span>' + esc(r.name) + (r.delivery_days && r.delivery_days.length ? ' <small>(' + esc(r.delivery_days.join('–')) + ' ' + esc(CS.days || 'days') + ')</small>' : '') + '</span><strong>' + price + '</strong></li>';
      }).join('') + '</ul>';
    }).catch(function () { out.innerHTML = '<p class="cart-tools__hint cart-tools__hint--error">' + esc(CS.shipError || 'Could not estimate shipping.') + '</p>'; });
  });
  // Discount codes: apply / remove via /cart/update.js { discount }
  function applyDiscounts(codes, panel, tried) {
    var msg = panel.querySelector('[data-discount-msg]');
    setCartBusy(true);
    return cartUpdate({ discount: codes.join(',') }).then(function (cart) {
      if (cart.status) throw new Error(cart.description || cart.message || 'Error');
      var applied = (cart.discount_codes || []);
      var bad = tried && !applied.some(function (d) { return d.code.toUpperCase() === tried.toUpperCase() && d.applicable; });
      if (bad) {
        // Drop the rejected code again so it doesn't linger as "not applicable"
        var keep = applied.filter(function (d) { return d.applicable; }).map(function (d) { return d.code; });
        if (msg) { msg.hidden = false; msg.className = 'cart-tools__hint cart-tools__hint--error'; msg.textContent = (CS.discountInvalid || '“[[code]]” isn’t valid for this bag.').replace('[[code]]', tried); }
        return cartUpdate({ discount: keep.join(',') }).then(function (c2) { setCartBusy(false); renderCart(c2); });
      }
      if (msg) { msg.hidden = !tried; msg.className = 'cart-tools__hint cart-tools__hint--ok'; if (tried) msg.textContent = (CS.discountApplied || '“[[code]]” applied.').replace('[[code]]', tried); }
      renderCart(cart);
      // The cart page renders line prices and the discount row server-side, so
      // re-render that section too; the parts swap leaves the panel — and this
      // "applied" message — in place. The rejected-code branch above skips it:
      // that path restores the cart to exactly what the page already shows.
      return updateCartPage().then(function () { setCartBusy(false); });
    }).catch(function (err) { setCartBusy(false); if (msg) { msg.hidden = false; msg.className = 'cart-tools__hint cart-tools__hint--error'; msg.textContent = (err && err.message) || 'Error'; } });
  }
  function currentCodes(cart) { return (cart.discount_codes || []).filter(function (d) { return d.applicable; }).map(function (d) { return d.code; }); }
  var lastCartCodes = [];
  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-cart-discount-form]');
    if (!form) return;
    e.preventDefault();
    var input = form.querySelector('[data-discount-input]'), code = (input.value || '').trim();
    if (!code) return;
    var panel = form.closest('[data-cart-tool-panel]');
    var codes = lastCartCodes.slice(); if (codes.map(function (c) { return c.toUpperCase(); }).indexOf(code.toUpperCase()) === -1) codes.push(code);
    applyDiscounts(codes, panel, code).then(function () { input.value = ''; });
  });
  document.addEventListener('click', function (e) {
    var rm = e.target.closest('[data-discount-remove]');
    if (!rm) return;
    var code = rm.getAttribute('data-discount-remove'), panel = rm.closest('[data-cart-tool-panel]');
    applyDiscounts(lastCartCodes.filter(function (c) { return c.toUpperCase() !== code.toUpperCase(); }), panel, null);
  });
  function renderDiscountCodes(cart) {
    lastCartCodes = currentCodes(cart);
    $all('[data-discount-codes]').forEach(function (ul) {
      ul.innerHTML = lastCartCodes.map(function (c) {
        return '<li class="cart-discount__code">' + esc(c) + '<button type="button" class="cart-discount__remove" data-discount-remove="' + esc(c) + '" aria-label="' + esc((CS.discountRemove || 'Remove [[code]]').replace('[[code]]', c)) + '">&times;</button></li>';
      }).join('');
      var btn = ul.closest('[data-cart-tools]') && ul.closest('[data-cart-tools]').querySelector('[data-cart-tool="discount"]');
      if (btn) btn.classList.toggle('has-value', lastCartCodes.length > 0);
    });
  }
  // Terms gate
  var TERMS_KEY = 'am-terms-ok';
  function syncTerms() {
    var boxes = $all('[data-cart-terms]');
    if (!boxes.length) return;
    var ok = false; try { ok = sessionStorage.getItem(TERMS_KEY) === '1'; } catch (e) {}
    boxes.forEach(function (b) { b.checked = ok; });
    $all('[data-cart-checkout]').forEach(function (btn) { btn.toggleAttribute('data-terms-blocked', !ok); btn.setAttribute('aria-disabled', !ok ? 'true' : 'false'); });
  }
  document.addEventListener('change', function (e) {
    var box = e.target.closest('[data-cart-terms]');
    if (!box) return;
    try { sessionStorage.setItem(TERMS_KEY, box.checked ? '1' : '0'); } catch (err) {}
    syncTerms();
  });
  // The tick the shopper needs is the one next to the button they pressed. Two
  // of them exist whenever the cart page is open: the drawer's (rendered in the
  // header, so it is first in the document) and the page's. A blanket
  // querySelector always found the drawer's — which is inert while the drawer
  // is closed, so focus() did nothing and the shake played inside a panel
  // nobody could see. Checkout was blocked with no visible reason. Walk up from
  // the button instead: the nearest ancestor that contains a terms box is the
  // surface the button belongs to.
  function termsBoxFor(btn) {
    var node = btn;
    while (node && node.nodeType === 1 && node !== document.body) {
      var box = node.querySelector('[data-cart-terms]');
      if (box) return box;
      node = node.parentNode;
    }
    return $('[data-cart-terms]');
  }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-cart-checkout]');
    if (!btn || !btn.hasAttribute('data-terms-blocked')) return;
    e.preventDefault();
    announce(CS.termsRequired || 'Please agree to the terms to continue.');
    var box = termsBoxFor(btn);
    if (!box) return;
    var row = box.closest('.cart-terms');
    if (row) {
      row.classList.add('is-shake');
      window.setTimeout(function () { row.classList.remove('is-shake'); }, 500);
    }
    // Never chase focus into a panel that is still inert.
    if (!box.closest('[inert]')) box.focus();
  });
  syncTerms();

  // Add-to-cart forms (product-form)
  // A form can own more than one submit control: the PDP's sticky bar button
  // lives outside the <form> and is bound to it with form="…". Resolving the
  // button with form.querySelector() only ever found the in-form one, so a tap
  // on the sticky button put "Adding…" on a button scrolled out of view and
  // left the sticky one live — an impatient second tap added the line twice.
  // form.elements includes controls associated by the form attribute, so this
  // collects every one of them.
  function addToCartControls(form) {
    var found = Array.prototype.filter.call(form.elements, function (el) {
      return el && el.hasAttribute && el.hasAttribute('data-add-to-cart');
    });
    if (!found.length) {
      var one = form.querySelector('[data-add-to-cart]');
      if (one) found = [one];
    }
    return found.map(function (btn) {
      var label = btn.querySelector('[data-add-label]') || btn;
      return { btn: btn, label: label, text: label.textContent, wasDisabled: btn.hasAttribute('disabled') };
    });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    // Belt and braces around the same double-add: implicit submission (Enter in
    // the quantity field) doesn't go through a button at all, so disabling the
    // buttons isn't enough on its own.
    if (form.hasAttribute('data-adding')) return;
    form.setAttribute('data-adding', '');
    var controls = addToCartControls(form);
    var pressed = e.submitter || (controls[0] && controls[0].btn) || null;
    function setLabel(text) { controls.forEach(function (c) { c.label.textContent = text; }); }
    controls.forEach(function (c) { c.btn.setAttribute('disabled', ''); });
    setLabel(T.strings.adding || 'Adding…');
    var errored = false;

    fetch(T.routes.cartAdd, {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: new FormData(form)
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (data.status) { throw new Error(data.description || 'Error'); }
        setLabel(T.strings.added || 'Added');
        announce(T.strings.added || 'Added to bag');
        return Promise.all([refreshCart(), updateCartPage()]).then(function () {
          if (T.cartType === 'drawer') {
            var drawerEl = $('#CartDrawer');
            if (openPanel !== drawerEl) openDrawer(drawerEl, pressed);
          } else {
            window.location.href = T.routes.cart;
          }
        });
      })
      .catch(function (err) {
        var msg = (err && err.message) || 'Error';
        setLabel(msg);
        announce(msg);
        errored = true;
      })
      .finally(function () {
        window.setTimeout(function () {
          form.removeAttribute('data-adding');
          controls.forEach(function (c) {
            // A control that was already disabled (sold-out variant) stays that way.
            if (!c.wasDisabled) c.btn.removeAttribute('disabled');
            c.label.textContent = c.text;
          });
        }, errored ? 4000 : 1200);
      });
  });

  /* --------------------------- Predictive search ------------------------ */
  // Recent searches. Recorded on the search results page itself, because that
  // is the one place every route into a search passes through: the modal, the
  // /search form, the 404 page's form, a predictive suggestion link and a
  // bookmark all land there. Per browser, never sent anywhere.
  var RECENT_KEY = 'am-recent-searches';
  var RECENT_MAX = 5;
  function readRecent() {
    try {
      var raw = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
      if (!Array.isArray(raw)) return [];
      return raw.filter(function (t) { return typeof t === 'string' && t.trim(); }).slice(0, RECENT_MAX);
    } catch (e) { return []; }
  }
  function writeRecent(list) {
    try { window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX))); } catch (e) {}
  }
  function rememberSearch(term) {
    term = String(term == null ? '' : term).replace(/\s+/g, ' ').trim().slice(0, 60);
    if (!term) return;
    var list = readRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
    list.unshift(term);
    writeRecent(list);
  }
  (function recordSearch() {
    var here = window.location.pathname.replace(/\/+$/, '');
    var searchPath = String(T.routes.search || '/search').split('?')[0].replace(/\/+$/, '');
    if (here !== searchPath) return;
    try { rememberSearch(new URLSearchParams(window.location.search).get('q')); } catch (e) {}
  })();

  (function predictiveSearch() {
    var forms = $all('[data-predictive-search]');
    if (!forms.length || !T.routes.predictiveSearch) return;
    forms.forEach(function (form) {
      var input = form.querySelector('input[type="search"], input[name="q"]');
      // Results and the start panel may sit next to the form (search modal)
      // rather than inside it.
      var scope = form.parentNode || document;
      var results = form.querySelector('[data-predictive-results]') || scope.querySelector('[data-predictive-results]');
      if (!input || !results) return;

      var start = scope.querySelector('[data-search-start]');
      var recentBlock = start && start.querySelector('[data-search-recent]');
      var recentList = start && start.querySelector('[data-search-recent-list]');
      // Whatever the merchant put in the panel. The recent block is the only
      // part this owns, so the panel can still earn its place with neither.
      var hasStatic = !!(start && start.querySelector('.search-start__block:not([data-search-recent])'));

      function renderRecent() {
        if (!recentBlock || !recentList) return;
        var list = readRecent();
        recentList.innerHTML = list.map(function (t) {
          return '<li><button type="button" class="search-start__chip" data-search-recent-term="' + esc(t) + '">' + esc(t) + '</button></li>';
        }).join('');
        recentBlock.hidden = list.length === 0;
      }
      function showStart() {
        if (!start) return;
        renderRecent();
        start.hidden = !(hasStatic || (recentBlock && !recentBlock.hidden));
      }

      var debounce, controller = null;
      function abortPending() {
        if (controller) { controller.abort(); controller = null; }
      }
      function clearResults() {
        abortPending();
        results.innerHTML = '';
        results.hidden = true;
        showStart();
      }
      function run(q) {
        abortPending();
        // Abort any in-flight request so a slow, stale response can't
        // overwrite the results for the newer query.
        var signal;
        if ('AbortController' in window) { controller = new AbortController(); signal = controller.signal; }
        var url = T.routes.predictiveSearch + '?q=' + encodeURIComponent(q) +
          '&resources[type]=product,collection,page,article,query&resources[limit]=6&resources[limit_scope]=each&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor,tag&section_id=predictive-search';
        fetch(url, { signal: signal }).then(function (r) { return r.text(); }).then(function (html) {
          if (input.value.trim() !== q) return; // stale (no AbortController fallback)
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var inner = doc.querySelector('[data-predictive-results-inner]');
          results.innerHTML = inner ? inner.innerHTML : '';
          results.hidden = false;
          if (start) start.hidden = true;
          if (window.AMWishlist) window.AMWishlist.sync(results);
          if (window.AMCompare) window.AMCompare.sync(results);
        }).catch(function () {});
      }

      input.addEventListener('input', function () {
        window.clearTimeout(debounce);
        var q = input.value.trim();
        if (q.length < 2) { clearResults(); return; }
        debounce = window.setTimeout(function () { run(q); }, 250);
      });

      if (start) {
        start.addEventListener('click', function (e) {
          if (e.target.closest('[data-search-recent-clear]')) {
            // Move focus off the button before the block it lives in goes away.
            input.focus();
            writeRecent([]);
            showStart();
            return;
          }
          var chip = e.target.closest('[data-search-recent-term]');
          if (!chip) return;
          var term = (chip.getAttribute('data-search-recent-term') || '').trim();
          input.value = term;
          input.focus();
          window.clearTimeout(debounce);
          if (term.length < 2) clearResults(); else run(term);
        });
        showStart();
      }
    });
  })();

  /* --------------------------- Reveal on scroll ------------------------- */
  (function reveal() {
    var els = $all('[data-reveal]');
    if (!els.length || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -80px' });
    var vh = window.innerHeight || document.documentElement.clientHeight;
    els.forEach(function (el) {
      // Anything already in the viewport at load is shown immediately so the
      // fade never delays first paint / LCP; only below-the-fold content reveals.
      if (el.getBoundingClientRect().top < vh) { el.classList.add('is-visible'); return; }
      io.observe(el);
    });
  })();

  /* --------------------------- Card swatch swap ------------------------- */
  // Clicking a color swatch on a product card swaps the card image and points
  // the card links at that variant.
  // The choice is also stamped on the card as data-card-variant so the card's
  // Quick view / "Choose options" buttons open on the color the shopper is
  // looking at. Those buttons only carry the product handle, so without it the
  // modal opened on the product's default variant: pick Red, press "Choose
  // options", get Black — with Black's price, Black's photo and Black's stock.
  document.addEventListener('click', function (e) {
    var sw = e.target.closest('[data-card-swatch]');
    if (!sw) return;
    e.preventDefault();
    var card = sw.closest('[data-product-card]');
    if (!card) return;
    $all('[data-card-swatch]', card).forEach(function (b) { b.classList.remove('is-active'); b.setAttribute('aria-pressed', 'false'); });
    sw.classList.add('is-active'); sw.setAttribute('aria-pressed', 'true');
    var img = card.querySelector('.card__img--primary');
    var src = sw.getAttribute('data-swatch-image');
    if (img && src) {
      img.src = src;
      var ss = sw.getAttribute('data-swatch-srcset');
      if (ss) img.srcset = ss; else img.removeAttribute('srcset');
      var hover = card.querySelector('.card__img--hover');
      if (hover) hover.style.display = 'none';
    }
    var url = sw.getAttribute('data-swatch-url');
    if (url) $all('a.card__media-link, a.card__title', card).forEach(function (a) { a.href = url; });
    var vid = sw.getAttribute('data-swatch-variant');
    if (vid) card.setAttribute('data-card-variant', vid);
    else card.removeAttribute('data-card-variant');
  });

  /* ------------------------ Quick view / quick edit --------------------- */
  // Loads sections/quick-view.liquid for a product into #QuickViewModal.
  // Modes: 'view' (add to bag) and 'edit' (replace a cart line: add the new
  // variant first, then remove the old line — the bag is never short).
  var QuickView = (function () {
    var modal = $('#QuickViewModal');
    if (!modal) return null;
    var body = modal.querySelector('[data-qv-body]');
    var mode = 'view', editLine = null;

    function load(url, opts) {
      opts = opts || {};
      mode = opts.mode || 'view';
      editLine = opts.line || null;
      body.innerHTML = '<p class="qv__loading">' + esc(T.strings.loading || 'Loading') + '…</p>';
      modal.classList.toggle('is-edit', mode === 'edit');
      openDrawer(modal, opts.trigger || null);
      var sep = url.indexOf('?') === -1 ? '?' : '&';
      var full = url + sep + 'section_id=quick-view' + (opts.variant ? '&variant=' + opts.variant : '');
      return fetch(full, { headers: { 'Accept': 'text/html' } })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var inner = doc.querySelector('[data-qv]');
          if (!inner) throw new Error('no qv');
          body.innerHTML = inner.outerHTML;
          init(body);
          if (window.AMWishlist) window.AMWishlist.sync(body);
          if (window.AMCountdown) window.AMCountdown.tick();
          var first = body.querySelector('input[data-qv-option]:checked') || body.querySelector(FOCUSABLE);
          if (first) first.focus();
        })
        .catch(function () {
          body.innerHTML = '<p class="customer__error">' + esc(T.strings.loadError || 'Could not load this product.') + '</p>';
        });
    }

    function init(root) {
      var form = root.querySelector('[data-qv-form]');
      if (!form) return;
      var variants = [];
      try { variants = JSON.parse(root.querySelector('[data-qv-variants]').textContent); } catch (e) {}
      var idInput = form.querySelector('[data-qv-id]');
      var priceEl = root.querySelector('[data-qv-price]');
      var img = root.querySelector('[data-qv-image]');
      var submit = form.querySelector('[data-qv-submit]');
      var submitLabel = form.querySelector('[data-qv-submit-label]');
      var qtyInput = form.querySelector('input[name="quantity"]');
      var colorEl = root.querySelector('[data-qv-selected-color]');
      if (mode === 'edit' && editLine) {
        if (qtyInput) qtyInput.value = editLine.qty || 1;
        if (submitLabel) submitLabel.textContent = T.strings.updateBag || 'Update bag';
      }
      function selected() {
        var opts = [];
        $all('input[data-qv-option]:checked', form).forEach(function (i) { opts[parseInt(i.getAttribute('data-qv-position'), 10)] = i.value; });
        return variants.find(function (v) { return v.options.every(function (o, i) { return o === opts[i]; }); }) || null;
      }
      function update() {
        var v = selected();
        if (!v) { if (submit) { submit.disabled = true; } if (submitLabel) submitLabel.textContent = T.strings.unavailable || 'Unavailable'; return; }
        if (idInput) idInput.value = v.id;
        if (priceEl) {
          // Mirrors snippets/price.liquid, labels included: <s> tells a screen
          // reader nothing, so a sale without them is two unlabelled numbers.
          var onSale = v.compare_at_price > v.price;
          var vhSale = onSale ? '<span class="visually-hidden">' + (T.strings.salePrice || 'Sale price') + ' </span>' : '';
          var vhWas = '<span class="visually-hidden">' + (T.strings.regularPrice || 'Regular price') + ' </span>';
          priceEl.innerHTML = '<span class="price' + (onSale ? ' price--sale' : '') + '"><span class="price__amount">' + vhSale + formatMoney(v.price) + '</span>' +
            (onSale ? ' <s class="price__compare">' + vhWas + formatMoney(v.compare_at_price) + '</s>' : '') + '</span>';
        }
        if (img && v.image) { img.src = v.image; img.removeAttribute('srcset'); }
        if (submit) submit.disabled = !v.available;
        if (submitLabel) submitLabel.textContent = !v.available ? (T.strings.soldOut || 'Sold out') : (mode === 'edit' ? (T.strings.updateBag || 'Update bag') : (T.strings.addToCart || 'Add to bag'));
        if (colorEl) {
          var ci = $all('fieldset', form).findIndex(function (f) { return f.classList.contains('pdp__option--color'); });
          if (ci > -1) colorEl.textContent = v.options[ci];
        }
      }
      form.addEventListener('change', function (e) { if (e.target.matches('[data-qv-option]')) update(); });
      var down = form.querySelector('[data-qv-qty-down]'), up = form.querySelector('[data-qv-qty-up]');
      if (down && qtyInput) down.addEventListener('click', function () { qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1); });
      if (up && qtyInput) up.addEventListener('click', function () { qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1; });
      update();
    }

    // Edit mode submit: replace the line instead of a plain add.
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-qv-form]');
      if (!form || mode !== 'edit' || !editLine) return;
      e.preventDefault(); e.stopImmediatePropagation();
      var newId = parseInt(form.querySelector('[data-qv-id]').value, 10);
      var qty = parseInt((form.querySelector('input[name="quantity"]') || {}).value, 10) || 1;
      var btn = form.querySelector('[data-qv-submit]'), label = form.querySelector('[data-qv-submit-label]'), err = form.querySelector('[data-qv-error]');
      if (btn) btn.disabled = true;
      if (label) label.textContent = T.strings.updating || 'Updating…';
      var done = function () { closeDrawer(); refreshCart().then(function () { openDrawer($('#CartDrawer')); }); };
      var fail = function (msg) { if (btn) btn.disabled = false; if (label) label.textContent = T.strings.updateBag || 'Update bag'; if (err) { err.textContent = msg; err.hidden = false; } };
      if (newId === parseInt(editLine.variant, 10)) {
        // Same variant: just update the quantity
        fetch(T.routes.cartChange, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ id: editLine.key, quantity: qty }) })
          .then(function (r) { return r.json(); }).then(function (d) { if (d.status) throw new Error(d.description || d.message); done(); })
          .catch(function (x) { fail(x.message || 'Error'); });
        return;
      }
      fetch(T.routes.cartAdd, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: newId, quantity: qty, properties: editLine.props || {} }] }) })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          if (d.status) throw new Error(d.description || d.message || 'Error');
          return fetch(T.routes.cartChange, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ id: editLine.key, quantity: 0 }) });
        })
        .then(function () { announce(T.strings.bagUpdated || 'Bag updated'); done(); })
        .catch(function (x) { fail(x.message || 'Error'); });
    }, true);

    document.addEventListener('click', function (e) {
      var qv = e.target.closest('[data-quick-view]');
      if (qv) {
        e.preventDefault();
        var handle = qv.getAttribute('data-quick-view');
        // If the shopper picked a swatch on this card, open the modal on that
        // variant. load() already supports it — data-line-edit uses the same
        // option to reopen a cart line on the variant it was bought in.
        var qvCard = qv.closest('[data-product-card]');
        load((T.routes.products || '/products') + '/' + handle, {
          mode: 'view', trigger: qv,
          variant: qvCard ? qvCard.getAttribute('data-card-variant') : null
        });
        return;
      }
      var ed = e.target.closest('[data-line-edit]');
      if (ed) {
        e.preventDefault();
        var props = {};
        try { props = JSON.parse(ed.getAttribute('data-line-props') || '{}'); } catch (x) {}
        load(ed.getAttribute('data-line-url'), {
          mode: 'edit', trigger: ed, variant: ed.getAttribute('data-line-variant'),
          line: { key: ed.getAttribute('data-line-key'), variant: ed.getAttribute('data-line-variant'), qty: parseInt(ed.getAttribute('data-line-qty'), 10) || 1, props: props }
        });
      }
    });

    return { load: load };
  })();
  window.AMQuickView = QuickView;

  /* --------------------------- Privacy choices -------------------------- */
  // Footer "Privacy choices": reopen Shopify's consent preferences when the
  // cookie banner is enabled (Privacy Banner API); otherwise follow the link.
  document.addEventListener('click', function (e) {
    var a = e.target.closest('[data-privacy-choices]');
    if (!a) return;
    var pb = window.privacyBanner;
    if (pb && typeof pb.showPreferences === 'function') { e.preventDefault(); pb.showPreferences(); }
  });

  /* ----------------------------- Price facet ---------------------------- */
  // Dual-handle price slider inside [data-price-facet] (snippets/facets.liquid).
  // The two range inputs drive the min/max number fields — which are the
  // controls that actually submit — and the fill follows both. Delegated on
  // document so it survives the collection page's AJAX swaps of the facet
  // markup and needs no wiring on /search, where the filter form is a plain
  // GET. Applying the filter stays the host page's job ('change' on the form).
  (function priceFacet() {
    function maxOf(wrap) { return parseFloat(wrap.getAttribute('data-max')) || 0; }
    function paint(wrap, a, b) {
      var slider = wrap.querySelector('[data-price-slider]'), max = maxOf(wrap);
      if (slider && max) { slider.style.setProperty('--lo', (a / max * 100) + '%'); slider.style.setProperty('--hi', (b / max * 100) + '%'); }
      // When the min thumb lives in the upper half, put it on top so it can still be grabbed when both thumbs meet at the max end.
      var lo = wrap.querySelector('[data-price-range="min"]');
      if (lo && max) lo.style.zIndex = a > max / 2 ? '2' : '';
    }
    document.addEventListener('input', function (e) {
      if (!e.target.closest) return;
      var wrap = e.target.closest('[data-price-facet]');
      if (!wrap) return;
      var lo = wrap.querySelector('[data-price-range="min"]'), hi = wrap.querySelector('[data-price-range="max"]');
      var nmin = wrap.querySelector('[data-price-input="min"]'), nmax = wrap.querySelector('[data-price-input="max"]');
      var max = maxOf(wrap);
      if (e.target.hasAttribute('data-price-range') && lo && hi) {
        var a = parseFloat(lo.value), b = parseFloat(hi.value);
        if (a > b) { if (e.target === lo) { hi.value = a; b = a; } else { lo.value = b; a = b; } }
        if (nmin) nmin.value = a > 0 ? a : '';
        if (nmax) nmax.value = b < max ? b : '';
        paint(wrap, a, b);
      } else if (e.target.hasAttribute('data-price-input') && lo && hi) {
        var va = nmin && nmin.value !== '' ? Math.min(Math.max(parseFloat(nmin.value) || 0, 0), max) : 0;
        var vb = nmax && nmax.value !== '' ? Math.min(Math.max(parseFloat(nmax.value) || max, 0), max) : max;
        if (va > vb) vb = va;
        lo.value = va; hi.value = vb;
        paint(wrap, va, vb);
      }
    });
  })();

  /* -------------------------------- Popups ------------------------------ */
  // Promo (delay) and before-you-leave (exit intent / idle) popups from the
  // Popups section group. Dismissal is remembered per popup id for
  // data-popup-expire days (localStorage → cookie fallback). Never shows while
  // another drawer is open, never on the cart/checkout, and never twice per
  // page. In the theme editor, opening the section shows it immediately.
  (function popups() {
    var els = $all('[data-popup]');
    if (!els.length) return;
    var shownThisPage = false;
    function storeGet(k) { try { return localStorage.getItem(k); } catch (e) { var m = document.cookie.match(new RegExp('(?:^|; )' + k.replace(/[-]/g, '\\-') + '=([^;]*)')); return m ? m[1] : null; } }
    function storeSet(k, v, days) { try { localStorage.setItem(k, v); } catch (e) {} document.cookie = k + '=' + v + '; max-age=' + (days * 86400) + '; path=/; SameSite=Lax'; }
    function dismissedUntil(el) { var v = parseInt(storeGet('am-popup:' + el.id) || '0', 10); return v && v > Date.now(); }
    function dismiss(el) { var days = parseInt(el.getAttribute('data-popup-expire'), 10) || 7; storeSet('am-popup:' + el.id, String(Date.now() + days * 86400000), days); }
    // Shopify hands the same posted_successfully? / errors state to EVERY
    // {% form 'customer' %} on the page, so the popup's newsletter form renders
    // a confirmation whenever the footer form was the one submitted. Liquid
    // can't tell them apart; the browser can. Remember on submit which popup's
    // form went out, and only that popup treats the state as its own.
    var SENT = 'am-popup-sent:';
    function sentGet(el) { try { return sessionStorage.getItem(SENT + el.id); } catch (e) { return null; } }
    function sentSet(el) { try { sessionStorage.setItem(SENT + el.id, '1'); } catch (e) {} }
    function sentClear(el) { try { sessionStorage.removeItem(SENT + el.id); } catch (e) {} }
    document.addEventListener('submit', function (e) {
      var form = e.target && e.target.closest ? e.target.closest('form') : null;
      var el = form && form.closest('[data-popup]');
      if (el) sentSet(el);
    }, true);
    function show(el) {
      if (shownThisPage || openPanel || dismissedUntil(el)) return;
      if (/\/(cart|checkouts?)\b/.test(location.pathname)) return;
      shownThisPage = true;
      openDrawer(el, null);
      // Any close counts as a dismissal for the expiry window
      var onClose = function () { if (!el.classList.contains('is-open')) { dismiss(el); obs.disconnect(); } };
      var obs = new MutationObserver(onClose);
      obs.observe(el, { attributes: true, attributeFilter: ['class'] });
    }
    els.forEach(function (el) {
      // Newsletter posted → keep it open on reload so the confirmation is seen,
      // but only when this popup's own form was the one submitted.
      var state = el.querySelector('[data-popup-success], [data-popup-error]');
      if (state) {
        if (sentGet(el)) {
          sentClear(el);
          if (!shownThisPage && !openPanel) { shownThisPage = true; window.setTimeout(function () { openDrawer(el, null); }, 50); }
          dismiss(el);
          return;
        }
        // Somebody else's submit. Drop the borrowed message and carry on as
        // if the page had rendered clean.
        if (state.parentNode) state.parentNode.removeChild(state);
      }
      if (dismissedUntil(el)) return;
      var trigger = el.getAttribute('data-popup-trigger');
      if (trigger === 'delay') {
        window.setTimeout(function () { show(el); }, (parseInt(el.getAttribute('data-popup-delay'), 10) || 0) * 1000);
      } else {
        // exit intent (desktop pointer) + idle timer (works on touch)
        var idleMs = (parseInt(el.getAttribute('data-popup-idle'), 10) || 30) * 1000, timer;
        var arm = function () { window.clearTimeout(timer); timer = window.setTimeout(function () { show(el); }, idleMs); };
        ['mousemove', 'keydown', 'scroll', 'touchstart', 'click'].forEach(function (ev) { document.addEventListener(ev, arm, { passive: true }); });
        arm();
        if (trigger === 'exit' && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          document.addEventListener('mouseout', function (e) { if (!e.relatedTarget && e.clientY <= 0) show(el); });
        }
      }
    });
    document.addEventListener('click', function (e) { var d = e.target.closest('[data-popup-dismiss]'); if (d) { var el = d.closest('[data-popup]'); if (el) dismiss(el); } });
    // Theme editor: show the popup being edited
    document.addEventListener('shopify:section:select', function (e) { var el = e.target && e.target.querySelector && e.target.querySelector('[data-popup]'); if (el) openDrawer(el, null); });
    document.addEventListener('shopify:section:deselect', function (e) { var el = e.target && e.target.querySelector && e.target.querySelector('[data-popup]'); if (el && openPanel === el) closeDrawer(); });
  })();

  /* ------------------------------ Countdown ----------------------------- */
  // [data-countdown="epochSeconds"] elements tick once a second (one shared
  // interval). data-countdown-hide removes the element when it ends;
  // otherwise the "ended" text is shown.
  (function countdown() {
    var UNITS = (window.theme && window.theme.strings && window.theme.strings.countdownUnits) || {};
    function pad(n) { return (n < 10 ? '0' : '') + n; }
    function tick() {
      var els = $all('[data-countdown]');
      if (!els.length) return;
      var now = Math.floor(Date.now() / 1000);
      els.forEach(function (el) {
        var end = parseInt(el.getAttribute('data-countdown'), 10) || 0;
        var left = end - now;
        if (left <= 0) {
          if (el.hasAttribute('data-countdown-hide')) { el.remove(); return; }
          el.classList.add('is-over');
          var units = el.querySelector('.countdown__units'), ended = el.querySelector('.countdown__ended');
          if (units) units.hidden = true;
          if (ended) ended.hidden = false;
          el.removeAttribute('data-countdown');
          return;
        }
        var d = Math.floor(left / 86400), h = Math.floor((left % 86400) / 3600), m = Math.floor((left % 3600) / 60), s = left % 60;
        var set = function (k, v) { var n = el.querySelector('[data-cd="' + k + '"]'); if (n && n.textContent !== v) n.textContent = v; };
        set('d', String(d)); set('h', pad(h)); set('m', pad(m)); set('s', pad(s));
        // Spelled-out unit labels (shown on desktop at the base size). The
        // snippet renders them plural; swap to the singular at exactly 1.
        // Left alone when the strings are absent, so the markup still reads.
        var word = function (k, v) {
          var pair = UNITS[k]; if (!pair) return;
          var n = el.querySelector('[data-cd-word="' + k + '"]'); if (!n) return;
          var t = v === 1 ? pair[0] : pair[1];
          if (n.textContent !== t) n.textContent = t;
        };
        word('d', d); word('h', h); word('m', m); word('s', s);
        if (d === 0) el.classList.add('countdown--no-days');
      });
    }
    tick();
    window.setInterval(tick, 1000);
    window.AMCountdown = { tick: tick };
  })();

  /* ------------------------------ Wishlist ------------------------------ */
  // Wishlist: product handles in localStorage. Any element with
  // [data-wishlist="handle"] toggles; [data-wishlist-count] shows the count;
  // the /pages/wishlist section renders saved products server-side via the
  // Section Rendering API (see sections/main-wishlist.liquid).
  //
  // Account sync: when the customer is signed in and the theme setting is on,
  // layout/theme.liquid sets T.listSync (app-proxy endpoint, e.g.
  // /apps/dell-rose/sync) and T.accountWishlist (the customer's
  // dell_rose.wishlist metafield, an array of {handle,...} snapshots, or
  // null). Every change is debounced and POSTed to the proxy, which is signed
  // by Shopify and written to the customer metafield by the crew-sync Worker.
  // On load the account copy replaces the device copy — unless the device
  // holds changes the account hasn't seen (made while signed out, or a push
  // that failed), in which case the two are merged (account first) and the
  // union is pushed back. Signed out / setting off: device-only, unchanged.
  //
  // Ownership: the device copy is stamped with the customer id it was last
  // reconciled for (am-wishlist-owner). When a different customer signs in
  // on the same browser, their account copy replaces the device copy
  // outright — no merge — so one shopper's saves are never pushed into
  // another shopper's metafield (shared/household devices). The merge runs
  // only when the stored owner is empty (signed-out saves) or matches.
  var Wishlist = (function () {
    // MAX is a platform ceiling, not a merchandising choice: the wishlist page
    // renders its cards from sections/wishlist-products.liquid, and Liquid
    // allows all_products only 20 lookups per render. Saves past the cap are
    // refused with a message — never accepted and then quietly dropped.
    var KEY = 'am-wishlist', MAX = 20;
    var DIRTY = 'am-wishlist-dirty', SYNCED = 'am-wishlist-synced', OWNER = 'am-wishlist-owner';
    var endpoint = typeof T.listSync === 'string' && T.listSync ? T.listSync : null;
    var customerId = T.customerId == null ? '' : String(T.customerId);
    var pushTimer = null;
    function fmtStr(str, n) { return String(str || '').replace('[[n]]', n); }
    function ls(get, k, v) { try { return get ? localStorage.getItem(k) : localStorage.setItem(k, v); } catch (e) { return null; } }
    function read() {
      try { var v = JSON.parse(localStorage.getItem(KEY) || '[]'); return Array.isArray(v) ? v : []; } catch (e) { return []; }
    }
    // The slice is a safety net, not the cap: every caller keeps the list at or
    // under MAX already (toggle() refuses at the cap, hydrate() only fills the
    // room that is left). It exists so an over-long account copy can never put
    // more handles on the device than sections/wishlist-products.liquid can
    // render — see MAX above.
    function store(list) { try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX))); } catch (e) {} }
    function payload(list) {
      return JSON.stringify({ key: 'wishlist', value: list.slice(0, MAX).map(function (h) { return { handle: h }; }) });
    }
    function push() {
      clearTimeout(pushTimer); pushTimer = null;
      if (!endpoint || typeof fetch !== 'function') return;
      var body = payload(read());
      fetch(endpoint, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: body })
        .then(function (r) { ls(false, DIRTY, r.ok ? '' : '1'); })
        .catch(function () { ls(false, DIRTY, '1'); });
    }
    function queue() {
      if (!endpoint) { ls(false, DIRTY, '1'); return; }
      clearTimeout(pushTimer);
      pushTimer = setTimeout(push, 900);
    }
    // Leaving the page with a push still queued: send it now.
    window.addEventListener('pagehide', function () {
      if (!endpoint || !pushTimer) return;
      clearTimeout(pushTimer); pushTimer = null;
      var body = payload(read());
      var sent = false;
      try { sent = navigator.sendBeacon && navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' })); } catch (e) {}
      if (sent) ls(false, DIRTY, ''); else ls(false, DIRTY, '1');
    });
    // Account copy vs device copy, once per page load (signed in only).
    function hydrate() {
      if (!endpoint) return;
      var acct = Array.isArray(T.accountWishlist)
        ? T.accountWishlist.map(function (it) { return it && typeof it === 'object' ? it.handle : it; })
            .filter(function (h) { return typeof h === 'string' && h; })
        : null;
      var owner = ls(true, OWNER) || '';
      if (owner && customerId && owner !== customerId) {
        // The device copy was last reconciled for a different customer:
        // discard it and take the account copy verbatim. Merging here is what
        // would write that shopper's saves into this customer's metafield.
        store(acct || []);
        ls(false, DIRTY, '');
        ls(false, SYNCED, '1');
        ls(false, OWNER, customerId);
        return;
      }
      var device = read();
      var dirty = ls(true, DIRTY) === '1' || ls(true, SYNCED) !== '1';
      var next, changed = false;
      if (acct === null) {
        // No account list yet: whatever is on this device becomes it.
        next = device; changed = device.length > 0;
      } else if (dirty) {
        // Union, account first; device-only handles appended into whatever room
        // is left under the cap. Filling the room (rather than appending and
        // then slicing) is what keeps the pushed list a superset of the account
        // list — otherwise a device save that didn't fit would be written back
        // as the deletion of an account save, on every device, permanently.
        next = acct.slice();
        var room = MAX - next.length;
        device.forEach(function (h) {
          if (room <= 0 || next.indexOf(h) !== -1) return;
          next.push(h); room--; changed = true;
        });
      } else {
        next = acct.slice(0, MAX);
      }
      if (next.join(',') !== device.join(',')) store(next);
      ls(false, SYNCED, '1');
      ls(false, OWNER, customerId);
      if (changed) push(); else ls(false, DIRTY, '');
    }
    function write(list) {
      store(list);
      if (customerId) ls(false, OWNER, customerId);
      sync();
      queue();
      document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles: list } }));
    }
    function has(handle) { return read().indexOf(handle) !== -1; }
    function full() { return read().length >= MAX; }
    // true = saved, false = removed, null = refused because the list is full.
    // null is falsy, so `if (toggle(h))` still reads as "was it saved?".
    function toggle(handle) {
      var list = read(), i = list.indexOf(handle);
      if (i !== -1) { list.splice(i, 1); write(list); return false; }
      if (list.length >= MAX) {
        announce(fmtStr(T.strings.wishlistFull || 'Your wishlist holds up to [[n]] items. Remove one to save another.', MAX));
        return null;
      }
      list.unshift(handle);
      write(list);
      return true;
    }
    function remove(handle) { write(read().filter(function (h) { return h !== handle; })); }
    function clear() { write([]); }
    function sync(root) {
      var list = read();
      var atCap = list.length >= MAX;
      var capMsg = fmtStr(T.strings.wishlistFull || 'Your wishlist holds up to [[n]] items. Remove one to save another.', MAX);
      // The tooltip gets the short version — the full sentence is a paragraph
      // in a bubble, and on a product card it would run out of the media box.
      // The long one still goes to aria-label and to the announcement on press.
      var capTip = T.strings.wishlistFullShort || capMsg;
      $all('[data-wishlist]', root).forEach(function (btn) {
        var on = list.indexOf(btn.getAttribute('data-wishlist')) !== -1;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        var title = btn.getAttribute('data-wishlist-title');
        // At the cap, hearts on unsaved products say so before they are pressed
        // rather than after. aria-disabled (not disabled) keeps them focusable,
        // so the reason is reachable by keyboard and the press still explains.
        // The reason goes in data-tooltip-alt, not title: these buttons carry a
        // CSS tooltip (see base.css) and a native title would sit on top of it.
        if (atCap && !on) {
          btn.setAttribute('aria-disabled', 'true');
          btn.setAttribute('data-tooltip-alt', capTip);
          btn.setAttribute('aria-label', capMsg);
          return;
        }
        btn.removeAttribute('aria-disabled');
        btn.removeAttribute('data-tooltip-alt');
        if (title && T.strings.wishlistSave && T.strings.wishlistSaved) {
          btn.setAttribute('aria-label', (on ? T.strings.wishlistSaved : T.strings.wishlistSave).replace('[[title]]', title));
        }
      });
      $all('[data-wishlist-count]').forEach(function (el) {
        el.textContent = list.length;
        el.hidden = list.length === 0;
      });
      $all('[data-wishlist-full]').forEach(function (el) { el.hidden = !atCap; });
    }
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-wishlist]');
      if (!btn) return;
      e.preventDefault();
      var on = toggle(btn.getAttribute('data-wishlist'));
      if (on === null) return; // list full — toggle() announced why, nothing changed
      var title = btn.getAttribute('data-wishlist-title') || '';
      announce(on ? (T.strings.wishlistSaved || 'Saved [[title]]').replace('[[title]]', title)
                  : (T.strings.wishlistRemoved || 'Removed from wishlist'));
    });
    hydrate();
    sync();
    window.addEventListener('storage', function (e) { if (e.key === KEY) sync(); });
    return { read: read, has: has, full: full, toggle: toggle, remove: remove, clear: clear, sync: sync, KEY: KEY, MAX: MAX, synced: !!endpoint };
  })();
  window.AMWishlist = Wishlist;

  /* ------------------------------- Compare ------------------------------ */
  // Product compare: handles in localStorage (max T.compareLimit). Toggle
  // buttons are [data-compare="handle"] (aria-pressed reflects state); one-shot
  // remove buttons inside the table are [data-compare][data-compare-remove].
  // A floating pill [data-compare-bar] shows the count and opens #CompareModal,
  // whose table is fetched from sections/compare-products.liquid via the
  // Section Rendering API (search route). /pages/compare renders the same
  // table inline (main-compare).
  var Compare = (function () {
    var KEY = 'am-compare', MAX = parseInt(T.compareLimit, 10) || 4;
    var bar = $('[data-compare-bar]');
    var modal = $('#CompareModal');
    var modalTable = modal ? modal.querySelector('[data-compare-modal-table]') : null;
    var modalLoading = modal ? modal.querySelector('[data-compare-modal-loading]') : null;
    var modalError = modal ? modal.querySelector('[data-compare-modal-error]') : null;
    var modalBody = modal ? modal.querySelector('[data-compare-modal-body]') : null;
    var modalKey = null, modalReq = 0;
    var S = T.strings;
    function fmt(str, n, title) { return String(str || '').replace('[[n]]', n).replace('[[title]]', title || ''); }
    function read() { try { var v = JSON.parse(localStorage.getItem(KEY) || '[]'); return Array.isArray(v) ? v : []; } catch (e) { return []; } }
    function write(list) {
      try { localStorage.setItem(KEY, JSON.stringify(list.slice(0, MAX))); } catch (e) {}
      sync();
      document.dispatchEvent(new CustomEvent('compare:change', { detail: { handles: list } }));
    }
    function modalOpen() { return !!(modal && modal.classList.contains('is-open')); }
    function setBusy(on) {
      if (modalBody) { if (on) modalBody.setAttribute('aria-busy', 'true'); else modalBody.removeAttribute('aria-busy'); }
      if (bar) bar.classList.toggle('is-loading', !!on);
      if (!on && modalLoading) modalLoading.hidden = true;
    }
    function rowsIn(root) { return $all('[data-compare-col]', root).map(function (el) { return el.getAttribute('data-compare-col'); }); }
    // Focus the closest sensible control after a row leaves the modal (the trap
    // no longer contains the removed button): the next/previous row's remove
    // button, else the modal's close button.
    function refocusAfterRemove(index) {
      if (!modalOpen()) return;
      var btns = $all('[data-compare-remove]', modalTable);
      var next = btns[Math.min(index, btns.length - 1)] || modal.querySelector('[data-drawer-close]');
      if (next) next.focus();
    }
    // Reconcile the modal with the current list: drop rows that left the list
    // right away (never leave stale, clickable rows), then (re)fetch the table
    // when the handle set changed. Refreshes are silent when rows are already
    // showing; the loading line only appears on a cold open.
    function fillModal(force) {
      if (!modal || !modalTable) return;
      var list = read(), key = list.join(',');
      if (!list.length) {
        modalReq++; // drop any in-flight response
        modalTable.innerHTML = '';
        modalKey = null;
        setBusy(false);
        if (modalError) modalError.hidden = true;
        if (modalOpen()) {
          closeDrawer();
          announce(S.compareCleared || 'Compare list cleared');
          // The pill is hidden now, so closeDrawer() had nothing to return focus to.
          var main = document.getElementById('MainContent') || document.querySelector('main');
          if (main) { if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1'); main.focus({ preventScroll: true }); }
        }
        return;
      }
      rowsIn(modalTable).forEach(function (h) {
        if (list.indexOf(h) === -1) { var row = modalTable.querySelector('[data-compare-col="' + h + '"]'); if (row) row.remove(); }
      });
      var hint = modalTable.querySelector('.compare__hint');
      if (list.length === 1 && !hint && S.compareAddMore && rowsIn(modalTable).length) modalTable.insertAdjacentHTML('afterbegin', '<p class="compare__hint">' + S.compareAddMore + '</p>');
      if (list.length > 1 && hint) hint.remove();
      if (!force && key === modalKey) return;
      var req = ++modalReq;
      modalKey = key;
      if (modalError) modalError.hidden = true;
      if (modalLoading) modalLoading.hidden = rowsIn(modalTable).length > 0;
      setBusy(true);
      fetch(T.routes.search + '?q=' + encodeURIComponent(key) + '&type=product&section_id=compare-products', { headers: { 'Accept': 'text/html' } })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          if (req !== modalReq || read().join(',') !== key) return; // superseded
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var inner = doc.querySelector('[data-compare-products]');
          if (!inner) throw new Error('bad-section'); // password/challenge page etc. — never treat as "no products"
          // Keep keyboard focus stable across the swap (silent refreshes replace the whole table).
          var act = document.activeElement, hadFocus = !!(act && modalTable.contains(act));
          var focusHandle = hadFocus && act.hasAttribute('data-compare-remove') ? act.getAttribute('data-compare') : null;
          modalTable.innerHTML = inner.innerHTML;
          sync(modalTable);
          if (hadFocus) {
            var again = focusHandle ? modalTable.querySelector('[data-compare-remove][data-compare="' + focusHandle + '"]') : null;
            (again || modalTable.querySelector('[data-compare-remove]') || modalBody).focus();
          }
          // Handles Shopify couldn't render (deleted/unpublished) fall out of the list, in one write.
          var got = rowsIn(modalTable);
          var kept = list.filter(function (h) { return got.indexOf(h) !== -1; });
          if (kept.length !== list.length) { modalKey = kept.join(','); write(kept); if (!kept.length) return; }
          announce(fmt(kept.length === 1 ? S.compareCountOne : S.compareCount, kept.length) || (kept.length + ' products'));
        })
        .catch(function () {
          if (req !== modalReq) return;
          modalKey = null;
          if (modalError) modalError.hidden = false;
          announce(S.compareLoadError || 'Could not load compare');
        })
        .then(function () { if (req === modalReq) setBusy(false); });
    }
    function openModal(trigger) {
      if (!modal) { window.location.href = '/pages/compare'; return; }
      fillModal(false);
      openDrawer(modal, trigger || null);
    }
    function has(h) { return read().indexOf(h) !== -1; }
    function add(h) {
      var list = read();
      if (list.indexOf(h) !== -1) return true;
      if (list.length >= MAX) { announce(fmt(S.compareFull || 'You can compare up to [[n]] products', MAX)); return false; }
      list.push(h);
      write(list); return true;
    }
    function remove(h) { write(read().filter(function (x) { return x !== h; })); }
    function clear() { write([]); }
    function toggle(h) { if (has(h)) { remove(h); return false; } return add(h); }

    /* --- Share a comparison ---------------------------------------------
       The compare list is device-local, which is fine for one shopper and
       useless for the people this store actually sells to: a booster picking
       one hoodie for a team, a room parent choosing between three tees. The
       link below carries the handles, so /pages/compare?ids=a,b,c rebuilds the
       same table for whoever opens it — the comparison becomes something a
       group can decide on together instead of something one phone remembers.
       Seeding only happens on /pages/compare; nowhere else reads ?ids=. */
    var SHARE_PARAM = 'ids';
    function shareUrl(list) {
      var handles = (list || read()).slice(0, MAX);
      if (!handles.length) return '';
      return window.location.origin + (T.routes.compare || '/pages/compare') + '?' + SHARE_PARAM + '=' + encodeURIComponent(handles.join(','));
    }
    // Handles arriving in the URL replace the device list: someone who follows
    // a shared link came to see those products, not the ones this browser
    // happened to be holding. Stripping the param afterwards means a later
    // edit survives a refresh instead of being reset to the link's contents.
    function seedFromUrl() {
      if (!document.querySelector('[data-compare-page]')) return false;
      var raw;
      try { raw = new URLSearchParams(window.location.search).get(SHARE_PARAM); } catch (e) { return false; }
      if (!raw) return false;
      var handles = raw.split(',').map(function (h) { return h.trim(); })
        .filter(function (h, i, a) { return h && /^[a-z0-9-]+$/i.test(h) && a.indexOf(h) === i; })
        .slice(0, MAX);
      if (window.history && window.history.replaceState) {
        try {
          var u = new URL(window.location.href);
          u.searchParams.delete(SHARE_PARAM);
          window.history.replaceState({}, '', u.pathname + u.search + u.hash);
        } catch (e) {}
      }
      if (!handles.length) return false;
      // Say the list was replaced even when the link happens to match what was
      // already here — the shopper still needs to know where this table came from.
      $all('[data-compare-shared]').forEach(function (el) { el.hidden = false; });
      if (handles.join(',') === read().join(',')) return false;
      write(handles);
      return true;
    }
    // Clipboard first; a hidden textarea + execCommand for the browsers (and
    // the non-secure contexts) where navigator.clipboard isn't there.
    function copyText(text) {
      if (navigator.clipboard && navigator.clipboard.writeText) return navigator.clipboard.writeText(text);
      return new Promise(function (resolve, reject) {
        var ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0';
        document.body.appendChild(ta);
        ta.select();
        var ok = false;
        try { ok = document.execCommand('copy'); } catch (e) {}
        document.body.removeChild(ta);
        ok ? resolve() : reject(new Error('copy'));
      });
    }
    var shareTimer = null;
    function shareClick(btn) {
      var url = shareUrl();
      if (!url) return;
      var label = btn.querySelector('[data-compare-share-label]') || btn;
      var original = btn.getAttribute('data-share-label') || label.textContent;
      btn.setAttribute('data-share-label', original);
      function settle(msg, ok) {
        label.textContent = msg;
        btn.classList.toggle('is-copied', !!ok);
        announce(msg);
        clearTimeout(shareTimer);
        shareTimer = setTimeout(function () { label.textContent = original; btn.classList.remove('is-copied'); }, 2500);
      }
      function copyFallback() {
        copyText(url)
          .then(function () { settle(S.compareShareCopied || 'Link copied', true); })
          .catch(function () {
            // Nothing reached the clipboard — don't claim it did. Put the link
            // somewhere it can be selected by hand rather than leaving a dead button.
            settle(S.compareShareError || 'Copy this link', false);
            window.prompt(S.compareShareError || 'Copy this link', url);
          });
      }
      // On a phone the point of this is the group chat, so hand the OS its own
      // share sheet when there is one. A cancelled sheet is not a failure and
      // must not fall through to a copy the shopper didn't ask for.
      if (navigator.share) {
        navigator.share({ title: document.title, url: url })
          .catch(function (err) { if (!err || err.name !== 'AbortError') copyFallback(); });
        return;
      }
      copyFallback();
    }
    function syncShare(list) {
      $all('[data-compare-share]').forEach(function (btn) { btn.hidden = !list.length; });
    }
    function sync(root) {
      var list = read();
      $all('[data-compare]:not([data-compare-remove])', root).forEach(function (btn) {
        var on = list.indexOf(btn.getAttribute('data-compare')) !== -1;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        var title = btn.getAttribute('data-compare-title');
        if (title && S.compareAdd && S.compareRemove) btn.setAttribute('aria-label', fmt(on ? S.compareRemove : S.compareAdd, null, title));
      });
      $all('[data-compare-count]').forEach(function (el) { el.textContent = list.length; });
      syncShare(list);
      if (bar) bar.hidden = list.length === 0 || !!document.querySelector('[data-compare-page]');
      if (modalOpen()) fillModal(false);
    }
    document.addEventListener('click', function (e) {
      var share = e.target.closest('[data-compare-share]');
      if (share) { e.preventDefault(); shareClick(share); return; }
      var opener = e.target.closest('[data-compare-open]');
      if (opener) { e.preventDefault(); openModal(opener); return; }
      var btn = e.target.closest('[data-compare]');
      if (btn) {
        e.preventDefault();
        var h = btn.getAttribute('data-compare'), title = btn.getAttribute('data-compare-title') || '';
        if (btn.hasAttribute('data-compare-remove')) {
          // One-shot remove inside the table: keep keyboard focus in the dialog and say what happened.
          var idx = $all('[data-compare-remove]', modalTable).indexOf(btn);
          remove(h);
          announce(fmt(S.compareRemoved || 'Removed [[title]] from compare', null, title));
          if (idx > -1) refocusAfterRemove(idx);
          return;
        }
        var on = toggle(h);
        announce(fmt(on ? (S.compareAdded || 'Added [[title]] to compare') : (S.compareRemoved || 'Removed [[title]] from compare'), null, title));
        return;
      }
      if (e.target.closest('[data-compare-clear]')) { e.preventDefault(); clear(); return; }
    });
    // seedFromUrl() writes (and therefore syncs) when a shared link brought
    // handles with it; sync() on its own otherwise.
    if (!seedFromUrl()) sync();
    window.addEventListener('storage', function (e) { if (e.key === KEY) { modalKey = null; sync(); } });
    return { read: read, has: has, add: add, remove: remove, clear: clear, toggle: toggle, set: function (list) { write((list || []).slice()); }, sync: sync, open: openModal, shareUrl: shareUrl, KEY: KEY, MAX: MAX };
  })();
  window.AMCompare = Compare;

  // Recently viewed (local history) — store current product handle
  (function recentlyViewed() {
    var meta = $('[data-product-handle]');
    if (!meta) return;
    var handle = meta.getAttribute('data-product-handle');
    try {
      var key = 'am-recently-viewed';
      var list = JSON.parse(localStorage.getItem(key) || '[]').filter(function (h) { return h !== handle; });
      list.unshift(handle);
      localStorage.setItem(key, JSON.stringify(list.slice(0, 12)));
    } catch (e) {}
  })();

  /* --------------------------- Deferred work ---------------------------- */
  // Non-critical work waits for the first interaction (or 3s) so first paint
  // and LCP aren't competing with it. Exposed as window.AMDefer for sections.
  var deferredFns = [], deferredFired = false;
  var deferEvents = ['mousemove', 'keydown', 'touchstart', 'pointerdown', 'scroll'];
  function fireDeferred() {
    if (deferredFired) return;
    deferredFired = true;
    deferEvents.forEach(function (ev) { window.removeEventListener(ev, fireDeferred); });
    deferredFns.splice(0).forEach(function (fn) { try { fn(); } catch (e) {} });
  }
  function deferUntilInteraction(fn) { if (deferredFired) { fn(); return; } deferredFns.push(fn); }
  deferEvents.forEach(function (ev) { window.addEventListener(ev, fireDeferred, { passive: true }); });
  window.setTimeout(fireDeferred, 3000);
  window.AMDefer = deferUntilInteraction;

  // Initial cart hydrate for count/free-shipping (header count is server-rendered, so this can wait)
  deferUntilInteraction(refreshCart);
  window.AMCart = { refresh: refreshCart, open: function (trigger) { openDrawer($('#CartDrawer'), trigger || null); } };
})();
